import React, { useMemo, useState } from 'react';
import { Shield, ShoppingCart, Monitor, Mail, Check, Minus } from 'lucide-react';
import { EMAIL_CATEGORIES, EMAIL_CATEGORY_DESCRIPTIONS } from '@platform/shared-types';
import { emailCategoryKeysFor } from './emailCategoriesForLine';

/**
 * WHAT THE MERCHANT CAN ACTUALLY DO — the same four tabs as the role editor at
 * /pos/access-management/create, in the small form an operator needs while the
 * box is open in front of them.
 *
 * READ-ONLY, and deliberately so. The role this shows is a WILDCARD
 * (`allowPosAdminAccess` / `allowPlatformAdminAccess`), expanded on every
 * request — so there is nothing here that is "off and could be switched on".
 * Anything the merchant cannot see is held back by their PACKAGE, which is not
 * a role setting and cannot honestly be fixed from this dialog. A toggle that
 * appeared to grant something and changed nothing would be worse than no
 * toggle: see `effective-access-get`, which resolves role + overrides with no
 * feature-gate intersection.
 */

/**
 * Labels and grouping for the POS buttons.
 *
 * MIRRORS `BUTTON_META` in platform-app's ButtonAccessGrid — a copy, because
 * that component derives its list from the tenant's own registers and package
 * features, which this screen has no business fetching. Only the label and the
 * group are needed here; an id with no entry falls back to its own id, so a
 * button added there and not here still appears rather than vanishing.
 */
const BUTTON_LABELS: Record<string, { label: string; group: string }> = {
  'void': { label: 'Void', group: 'Sales' },
  'refund': { label: 'Refund', group: 'Sales' },
  'exchange': { label: 'Exchange', group: 'Sales' },
  'discount': { label: 'Discount', group: 'Sales' },
  'hold': { label: 'Hold', group: 'Sales' },
  'open-order': { label: 'Open order', group: 'Sales' },
  'search': { label: 'Search', group: 'Sales' },
  'customer': { label: 'Customer', group: 'Sales' },
  'rewards': { label: 'Rewards', group: 'Sales' },
  'coupon': { label: 'Coupon', group: 'Sales' },
  'transactions': { label: 'Transactions', group: 'Sales' },
  'cash-tip': { label: 'Cash tip', group: 'Sales' },
  'deals': { label: 'Deals', group: 'Sales' },
  'damage': { label: 'Damage', group: 'Sales' },
  'store-supply': { label: 'Store supply', group: 'Sales' },
  'weighted-lookup': { label: 'Weighted lookup', group: 'Sales' },
  'non-scannable-lookup': { label: 'Non-scannable lookup', group: 'Sales' },
  'custom-item': { label: 'Custom item', group: 'Sales' },
  'employee-discount': { label: 'Employee discount', group: 'Sales' },
  'new-product-request': { label: 'New product request', group: 'Sales' },
  'open-drawer': { label: 'Open drawer', group: 'Register' },
  'safe-drop': { label: 'Safe drop', group: 'Register' },
  'payout': { label: 'Payout', group: 'Register' },
  'calculator': { label: 'Calculator', group: 'Register' },
  'pick-list': { label: 'Pick list', group: 'Register' },
  'transfer': { label: 'Transfer', group: 'Register' },
  'reports': { label: 'Reports', group: 'Admin' },
  'clock': { label: 'Clock in / out', group: 'Employee' },
  'shift-info': { label: 'Shift info', group: 'Employee' },
  'end-shift': { label: 'End shift', group: 'Employee' },
};

const GROUP_ORDER = ['Sales', 'Register', 'Employee', 'Admin', 'Other'];

type TabId = 'overview' | 'pos' | 'online' | 'email';

const TABS: { id: TabId; label: string; icon: React.ReactNode }[] = [
  { id: 'overview', label: 'Overview', icon: <Shield size={13} /> },
  { id: 'pos', label: 'POS', icon: <ShoppingCart size={13} /> },
  { id: 'online', label: 'Online', icon: <Monitor size={13} /> },
  { id: 'email', label: 'Email', icon: <Mail size={13} /> },
];

/** One granted line. Every row here is on — the role is a wildcard. */
const Granted: React.FC<{ title: string; note?: string }> = ({ title, note }) => (
  <div className="flex items-start gap-2.5 border-b border-stone-100 py-2 last:border-0">
    <span className="mt-0.5 grid h-[17px] w-[17px] shrink-0 place-items-center rounded-full bg-emerald-500 text-white">
      <Check size={11} strokeWidth={3} />
    </span>
    <div className="min-w-0">
      <p className="text-[12.5px] text-stone-800">{title}</p>
      {note && <p className="mt-0.5 text-[11px] leading-relaxed text-stone-500">{note}</p>}
    </div>
  </div>
);

const AccessVerifyTabs: React.FC<{
  roleName: string;
  posButtons: string[];
  /** The tenant's product line. Missing ⇒ Ruby Till (small-fixes item 27). */
  productLine?: string | null;
}> = ({ roleName, posButtons, productLine }) => {
  const [tab, setTab] = useState<TabId>('overview');

  const grouped = useMemo(() => {
    const by = new Map<string, { id: string; label: string }[]>();
    for (const id of posButtons) {
      const meta = BUTTON_LABELS[id];
      const group = meta?.group || 'Other';
      if (!by.has(group)) by.set(group, []);
      by.get(group)!.push({ id, label: meta?.label || id });
    }
    return GROUP_ORDER.filter((g) => by.has(g)).map((g) => ({ group: g, items: by.get(g)! }));
  }, [posButtons]);

  return (
    <>
      {/* Emerald pills — the platform's tab standard. */}
      <div className="flex flex-wrap gap-1.5 border-b border-stone-200 px-5 pb-3 pt-1">
        {TABS.map((t) => (
          <button
            key={t.id}
            onClick={() => setTab(t.id)}
            className={`inline-flex min-h-[34px] items-center gap-1.5 rounded-full px-3 text-[12px] font-semibold transition-colors ${
              tab === t.id
                ? 'bg-emerald-600 text-white'
                : 'bg-stone-100 text-stone-600 hover:bg-stone-200'}`}
          >
            {t.icon}{t.label}
          </button>
        ))}
      </div>

      <div className="max-h-[54vh] overflow-y-auto px-5 py-4">
        <p className="mb-3 rounded-lg border-l-[3px] border-emerald-500 bg-emerald-50 px-3 py-2 text-[12px] leading-relaxed text-stone-700">
          <strong>{roleName} grants everything, now and in future.</strong> These are rules, not
          lists — a feature added next month is covered with no change here. What the merchant
          actually sees is still limited by their package, which is not a role setting.
        </p>

        {tab === 'overview' && (
          <div>
            <Granted title="POS admin access" note="Every till button, including any added later." />
            <Granted title="Platform admin access" note="Every back-office menu, including any added later." />
            <Granted title="Marketplace owner access" />
            <Granted title="AI agent / MCP access" />
            <Granted title="AI inventory alerts" note="And the emails that go with them." />
            <div className="mt-3 rounded-lg bg-stone-50 px-3 py-2.5 text-[11.5px] leading-relaxed text-stone-500">
              This is the owner's role for this location. Staff roles are created by the merchant
              in their own Access Management, and can be narrower than this one.
            </div>
          </div>
        )}

        {tab === 'pos' && (
          <div>
            <p className="mb-2 text-[11px] font-bold uppercase tracking-[0.12em] text-stone-400">
              {posButtons.length} till buttons granted today
            </p>
            {grouped.map(({ group, items }) => (
              <div key={group} className="mb-3">
                <p className="mb-1 text-[11.5px] font-semibold text-stone-700">{group}</p>
                {items.map((b) => <Granted key={b.id} title={b.label} />)}
              </div>
            ))}
            {posButtons.length === 0 && (
              <p className="text-[12.5px] text-stone-500">
                No buttons resolved — the role is set, but the permission catalog came back empty.
              </p>
            )}
          </div>
        )}

        {tab === 'online' && (
          <div>
            <Granted
              title="Every back-office menu"
              note="Products, stock, reports, staff, settings and anything added later — the platform-admin rule covers the whole menu rather than listing it."
            />
            <div className="mt-3 rounded-lg bg-stone-50 px-3 py-2.5 text-[11.5px] leading-relaxed text-stone-500">
              Menus for features the merchant's package does not include stay hidden. Upgrading the
              package reveals them with no change to this role.
            </div>
          </div>
        )}

        {tab === 'email' && (
          <div>
            <p className="mb-2 text-[11px] font-bold uppercase tracking-[0.12em] text-stone-400">
              Email categories this role receives
            </p>
            {emailCategoryKeysFor(productLine).map((k) => (
              <Granted key={k} title={EMAIL_CATEGORIES[k]} note={EMAIL_CATEGORY_DESCRIPTIONS[k]} />
            ))}
            <div className="mt-3 flex items-start gap-2 rounded-lg bg-stone-50 px-3 py-2.5 text-[11.5px] leading-relaxed text-stone-500">
              <Minus size={13} className="mt-0.5 shrink-0" />
              <span>The owner can turn any of these off for themselves on their own profile.</span>
            </div>
          </div>
        )}
      </div>
    </>
  );
};

export default AccessVerifyTabs;
